import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Sun, Battery, Lightbulb, Zap, Timer, Thermometer } from 'lucide-react'

const Technology = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const steps = [
    {
      icon: Sun,
      title: "Light Absorption",
      description: "Pigmentium™ particles absorb and store photons from any light source in just 5 seconds",
      details: "Advanced photoluminescent materials capture light energy and convert it to stored luminous energy"
    },
    {
      icon: Battery,
      title: "Energy Storage",
      description: "Captured light energy is stored at the molecular level for extended release",
      details: "Proprietary molecular structure maintains energy stability for 12+ hours of continuous emission"
    },
    {
      icon: Lightbulb,
      title: "Light Emission",
      description: "Stored energy is gradually released as 500 Lux brightness for up to 12 hours",
      details: "Controlled energy release provides consistent, high-quality illumination throughout the night"
    }
  ]

  const specifications = [
    { label: "Charging Time", value: "5 seconds", icon: Timer },
    { label: "Light Output", value: "500 Lux", icon: Lightbulb },
    { label: "Duration", value: "12+ hours", icon: Battery },
    { label: "Operating Temp", value: "-20°C to +60°C", icon: Thermometer },
    { label: "Energy Source", value: "Any light", icon: Sun },
    { label: "Power Consumption", value: "0 Watts", icon: Zap }
  ]

  return (
    <section id="technology" className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            How <span className="text-yellow-400">Pigmentium™</span> Works
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Revolutionary photoluminescent technology that captures, stores, and releases light energy 
            without any electrical power consumption.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12 mb-20">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.3 }}
              className="text-center"
            >
              <div className="relative mb-8">
                <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-2xl">
                  <step.icon className="w-12 h-12 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-yellow-400 to-transparent"></div>
                )}
              </div>
              <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
              <p className="text-lg text-gray-300 mb-4">{step.description}</p>
              <p className="text-sm text-gray-400 leading-relaxed">{step.details}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-16"
        >
          <h3 className="text-3xl font-bold text-center mb-12">Technical Specifications</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {specifications.map((spec, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 1 + index * 0.1 }}
                className="text-center p-6 bg-white/5 rounded-xl hover:bg-white/10 transition-all duration-300"
              >
                <spec.icon className="w-10 h-10 text-yellow-400 mx-auto mb-4" />
                <h4 className="text-lg font-semibold mb-2">{spec.label}</h4>
                <p className="text-2xl font-bold text-yellow-400">{spec.value}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="grid lg:grid-cols-2 gap-12 items-center"
        >
          <div>
            <h3 className="text-3xl font-bold mb-6">The Science Behind Pigmentium™</h3>
            <div className="space-y-6">
              <div className="border-l-4 border-yellow-400 pl-6">
                <h4 className="text-xl font-semibold mb-2">Photoluminescent Materials</h4>
                <p className="text-gray-300">Advanced phosphorescent compounds that efficiently capture and store photon energy from any light source.</p>
              </div>
              <div className="border-l-4 border-blue-400 pl-6">
                <h4 className="text-xl font-semibold mb-2">Molecular Energy Storage</h4>
                <p className="text-gray-300">Proprietary molecular structures maintain energy at quantum levels for extended periods without degradation.</p>
              </div>
              <div className="border-l-4 border-green-400 pl-6">
                <h4 className="text-xl font-semibold mb-2">Controlled Emission</h4>
                <p className="text-gray-300">Engineered emission curves provide consistent light output throughout the entire 12-hour period.</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-2xl p-8">
            <h4 className="text-2xl font-bold mb-6 text-center">Performance Comparison</h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span>Traditional LED</span>
                <span className="text-red-400">24/7 Power Required</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Solar + Battery</span>
                <span className="text-yellow-400">6-8 Hour Runtime</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Pigmentium™</span>
                <span className="text-green-400">12+ Hours, 0 Power</span>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full mt-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 font-semibold py-3 px-6 rounded-xl hover:shadow-lg transition-all duration-300"
            >
              Download Technical Datasheet
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Technology 